"use client";

import Script from "next/script";
import { useEffect, useState } from "react";

/**
 * EasyChatWidget — the floating support chat bubble on the marketing site.
 *
 * Scoping matters, because this lives in the ROOT layout, which also wraps
 * the POS, brand storefronts, checkout and platform admin. It gates on an
 * EXACT host match, so the widget only ever loads on the marketing site:
 * admin.kuttostechnologies.com, platform-admin.*, every brand subdomain and
 * every custom domain are different hosts and get nothing.
 *
 * Host is the right gate rather than path, because a path check can't hold
 * in a single-page app: once a third-party script has executed it owns its
 * own DOM, and React unmounting this <Script> doesn't take the bubble with
 * it. Crossing a hostname is a fresh document, so the gate is real.
 *
 * Note this is a third-party script and, like any embedded widget, it can
 * read the page it runs on. That's fine for marketing pages; it's the reason
 * it must never reach a POS or checkout surface.
 *
 * `lazyOnload` runs it after everything else, so it costs the landing page
 * no paint time.
 */

const WIDGET_ID = "HdVvnX74dPJBXCh3HOdLjPTGNvGSZJbR";

const MARKETING_HOSTS = new Set([
  "kuttostechnologies.com",
  "www.kuttostechnologies.com",
  "kuttostechnologies.vercel.app",
  "localhost",
]);

export function EasyChatWidget() {
  // Host is only knowable in the browser, and reading it during render would
  // make the server and client disagree.
  const [host, setHost] = useState<string | null>(null);
  useEffect(() => setHost(window.location.hostname), []);

  if (!host || !MARKETING_HOSTS.has(host)) return null;

  return (
    <Script
      src="https://easychatwidget.com/widget.js"
      data-widget-id={WIDGET_ID}
      strategy="lazyOnload"
    />
  );
}
